// @flow
import React, { type Node } from 'react';
import Helmet from 'react-helmet-async';
import { compose, pure } from 'recompose';
import { connect } from 'react-redux';
import { angiLeadsUtils } from 'angieslist-office-app-common/lib/util/angiLeadsUtils';
import { Alert, Button, Col, Grid, Input, Row, Well, ReduxFormPhoneNumber } from 'angieslist-react-components';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import { reduxForm, Field } from 'redux-form';
import { LegacyTrack, urlCatalog, validation } from 'angieslist-app-platform';
import { get } from 'lodash';
import * as appDownloadActions from '../../common/download/appDownloadActions';
import './downloadPrompt.scss';

const iosIcon = require('../app-store-apple.png');
const androidIcon = require('../app-store-android.png');
const angiAdsProAppImage = require('../phones.png');

const formValidationRules = {
  contactNumber: {
    required: true,
    phoneNumber: true,
    msg: 'That is not a valid number. Please try again',
  },
  os: {
    required: true,
    msg: 'Please make a selection',
  },
};

type FieldProps = {
  input: any,
  meta: {
    touched: boolean,
    error: any,
  }
}

const RadioField = ({ input, meta: { touched, error } }: FieldProps) => (
  <div className={`radio-group ${touched && error ? 'has-error' : ''}`} style={{ position: 'relative', bottom: -5 }}>
    <Input {...input} type="radio" value="ios" label="iOS" id="app-download--ios-radio" name="os" style={{ margin: 5 }} checked={input.value === 'ios'} />
    <Input {...input} type="radio" value="android" label="Android" id="app-download--android-radio" name="os" style={{ margin: 5 }} checked={input.value === 'android'} />
    <If condition={touched && error}>
      <div className="help-block feedback-custom-help-block">{error}</div>
    </If>
  </div>
);

const homePageMessage = isOnAngiLeads => (
  <p className="text-primary success-message">
    <i className="icon icon-check icon-2x pull-left" />
    <small>
      Message sent. While you&apos;re waiting, check out the {
        <LegacyTrack>
          <a
            id="app-download--success-home-link"
            className="text-info"
            href={urlCatalog.office.home()}
          >
            {`${isOnAngiLeads ? 'NEW Angi Leads (previously HomeAdvisor) site' : 'Business Center'}`}
          </a>
        </LegacyTrack>
      }
    </small>
  </p>
);

class DownloadPromptComponent extends React.Component<*> {
  static defaultProps: Object;
  props: {
    header: string,
    subheader: string,
    showEscapeLink: boolean,
    serviceProviderId?: number,
    referrer: string,
    handleSubmit: any,
    sendAppLink: (
      os: string, contactNumber: string, platformType?: string, referrer?: string
    ) => any,
    appDownload: any,
    reset: () => void,
    pristine: boolean,
    invalid: boolean,
  }

  // eslint-disable-next-line react/no-unused-prop-types
  submitFn: (({contactNumber: string, os: string}) => any) =
  ({ os, contactNumber }) => {
    const { sendAppLink, referrer, reset } = this.props;
    const platformType = angiLeadsUtils.isOnAngiLeadsPage() ? 'Angi Leads' : 'Angi Ads';
    return sendAppLink(os, contactNumber, platformType, referrer).then(reset);
  }

  render(): Node {
    const {
      header, subheader, appDownload, handleSubmit, referrer, serviceProviderId, showEscapeLink,
      pristine, invalid,
    } = this.props;
    const linkSent = get(this.props, 'appDownload.response.success');
    const isOnAngiLeads = angiLeadsUtils.isOnAngiLeadsPage(typeof window !== 'undefined' ? window.location.pathname : '');
    const angiLeadsProAppImage = 'https://media.angi.com/s3fs-public/Angi-Pro-Leads-app.png';

    const androidAppUrl = isOnAngiLeads
      ? urlCatalog.apps.office.angiLeadsAndroid()
      : urlCatalog.apps.office.angiAdsAndroid(referrer);
    const iOSAppUrl = isOnAngiLeads
      ? urlCatalog.apps.office.angiLeadsiOS()
      : urlCatalog.apps.office.angiAdsiOS(referrer);

    return (
      <Grid className="download-prompt">
        <Helmet title="Pro App Download" />
        <Row>
          <Col xs={12} sm={6} smPush={6} md={7} mdPush={5}>
            <h1 className="download-header">{header}</h1>
            <p className="text-center visible-xs">{subheader}</p>
            <div className="hidden-xs">
              <h4>{subheader}</h4>
              <ul style={{ paddingLeft: '1.2em', marginBottom: '1.5em' }}>
                <li>Manage your business leads with ease</li>
                <li>Respond to messages and request reviews directly from your phone</li>
                <li>{`Track your performance on Angi ${isOnAngiLeads ? 'Leads' : 'Ads'}`}</li>
              </ul>
            </div>
            <div className="hidden-xs">
              <Well>
                <h5 style={{ marginBottom: '.5em', marginTop: '.5em' }}>Text a Download Link to Your Phone</h5>
                <p className="small" style={{ marginBottom: '1em' }}>Enter your mobile number to receive a download link directly to your device.</p>
                <Loader
                  loading={appDownload.isLoading}
                  element={
                    <Row className="text-center">
                      <i className="icon icon-load icon-spin icon-3x text-primary" />
                    </Row>
                  }
                >
                  <div>
                    <If condition={appDownload.error}>
                      <Alert bsStyle="danger">There was an error sending the link. Let us help you by contacting <a id="app-download--error-phone-link" href={urlCatalog.global.businessCenterPhone()}>{urlCatalog.global.businessCenterPhoneText()}</a>.</Alert>
                    </If>
                    <form name="send-sms-link" onSubmit={handleSubmit(this.submitFn)}>
                      <Row>
                        <Col sm={9} md={6} lg={5}>
                          <ReduxFormPhoneNumber
                            name="contactNumber"
                            id="app-download--sms-number"
                            label="Mobile Number"
                          />
                        </Col>
                        <Col sm={3} md={3} className="os-radio-col">
                          <Field
                            id="app-download--app-os"
                            name="os"
                            component={RadioField}
                          />
                        </Col>
                        <Col sm={12} md={3} style={{ paddingTop: 24 }}>
                          <LegacyTrack>
                            <Button
                              id="app-download--send-sms-button"
                              flat
                              block
                              bsSize="small"
                              bsStyle="primary-office"
                              type="submit"
                              disabled={pristine || invalid}
                              data-track-spid={serviceProviderId}
                            >
                              Send Link
                            </Button>
                          </LegacyTrack>
                        </Col>
                      </Row>
                    </form>
                    <If condition={linkSent}>
                      {homePageMessage(isOnAngiLeads)}
                    </If>
                  </div>
                </Loader>
              </Well>
            </div>
            <div className="link-container">
              <LegacyTrack>
                <a
                  href={iOSAppUrl}
                  id="app-download--link-ios"
                  target="blank"
                  data-track-spid={serviceProviderId}
                >
                  <img
                    alt={`Download the Angi Pro ${isOnAngiLeads ? 'Leads' : 'Ads'} App for iOS`}
                    src={iosIcon}
                  />
                </a>
              </LegacyTrack>
              <LegacyTrack>
                <a
                  href={androidAppUrl}
                  id="app-download--link-android"
                  target="blank"
                  data-track-spid={serviceProviderId}
                >
                  <img
                    alt={`Download the Angi Pro ${isOnAngiLeads ? 'Leads' : 'Ads'} App for Android`}
                    src={androidIcon}
                  />
                </a>
              </LegacyTrack>
            </div>
            <If condition={showEscapeLink}>
              <LegacyTrack>
                <a
                  id="app-download--escape-link"
                  data-track-spid={serviceProviderId}
                  className="text-info hidden-xs"
                  href={urlCatalog.office.home()}
                >No thanks, take me to the Business Center
                </a>
              </LegacyTrack>
            </If>
          </Col>
          <Col xs={12} sm={5} smPull={6} md={4} mdPull={7} className="text-center screens-container">
            <img
              alt="Angi Pro App"
              src={isOnAngiLeads ? angiLeadsProAppImage : angiAdsProAppImage}
              className="screens"
            />
            <If condition={showEscapeLink}>
              <div className="visible-xs no-thanks-div">
                <LegacyTrack>
                  <a
                    id="app-download--escape-link"
                    data-track-spid={serviceProviderId}
                    href={urlCatalog.office.home()}
                    className="text-info"
                  >No thanks, take me to the Business Center
                  </a>
                </LegacyTrack>
              </div>
            </If>
          </Col>
        </Row>
      </Grid>
    );
  }
}

DownloadPromptComponent.defaultProps = {
  serviceProviderId: undefined,
};

export { DownloadPromptComponent };

export default (compose(
  reduxForm({
    form: 'send-sms-link',
    validate: values => validation.validate(values, formValidationRules).invalidAttrs,
  }),
  connect(
    state => ({
      appDownload: state.appDownload,
    }),
    { ...appDownloadActions },
  ),
  pure,
)(DownloadPromptComponent): any);



// WEBPACK FOOTER //
// ./src/app/browser/download/DownloadPrompt.js